import { reportAPI } from '@/services-ts/axios'
import type { IDashboardSummary } from '@/types/Data'
import type {
  IDateRange,
  IGetAccessReport,
  IGetCompanyValueReport,
  IGetDashboardSummary,
  IGetEmployeeInteractingByDepartmentsReport,
  IGetMobileSignedInUsers,
  IGetMostInteractiveEmployeeReport,
  IGetPortalLoginReport,
  IGetPostTypeReport,
  IGetTemporalChangeReport,
  IGetTemporalChangeReportExcel,
} from '@/types/Request'
import { defineStore } from 'pinia'

const emptySummary = (): IDashboardSummary => ({
  sum: 0,
  previousSum: 0,
  value: 0,
  previousValue: 0,
  changePercentage: 0,
})

export const useDashboardStore = defineStore('dashboard', {
  actions: {
    async GetMostInteractiveEmployeeReport(payload: IGetMostInteractiveEmployeeReport) {
      const { status, data } =
        await reportAPI.DashboardServices.GetMostInteractiveEmployeeReport(payload)
      return status ? data : []
    },

    async GetEmployeesInteractingByDepartmentsReport(
      payload: IGetEmployeeInteractingByDepartmentsReport,
    ) {
      const { status, data } =
        await reportAPI.DashboardServices.GetEmployeesInteractingByDepartmentsReport(payload)
      return status ? data : []
    },

    async GetCompanyValueReport(payload: IGetCompanyValueReport) {
      const { status, data } = await reportAPI.DashboardServices.GetCompanyValueReport(payload)
      return status ? data : []
    },

    async GetPostTypeReport(payload: IGetPostTypeReport) {
      const { status, data } = await reportAPI.DashboardServices.GetPostTypeReport(payload)
      return status ? data : []
    },

    async GetPortalLoginReport(payload: IGetPortalLoginReport) {
      const { status, data } = await reportAPI.DashboardServices.GetPortalLoginReport(payload)
      return status ? data?.items || [] : []
    },

    async GetPortalLoginReportExcel(payload: IGetPortalLoginReport) {
      await reportAPI.DashboardServices.GetPortalLoginReportExcel(payload)
    },

    async GetAccessReport(payload: IGetAccessReport) {
      const { status, data } = await reportAPI.DashboardServices.GetAccessReport(payload)
      return status ? data : null
    },

    async GetAccessReportExcel(payload: IDateRange) {
      await reportAPI.DashboardServices.GetAccessReportExcel(payload)
    },

    async GetTemporalChangeReport(payload: IGetTemporalChangeReport) {
      const { status, data } = await reportAPI.DashboardServices.GetTemporalChangesReport(payload)
      return status ? data : null
    },

    async GetTemporalChangeReportExcel(payload: IGetTemporalChangeReportExcel[]) {
      await reportAPI.DashboardServices.GetTemporalChangeReportExcel(payload)
    },

    // Cards
    async GetPostSummary(payload: IGetDashboardSummary) {
      const { data } = await reportAPI.DashboardServices.GetPostSummary(payload)
      return data || emptySummary()
    },
    async GetSpentPointReport(payload: IGetDashboardSummary) {
      const { data } = await reportAPI.DashboardServices.GetSpentPointReport(payload)
      return data || emptySummary()
    },
    async GetEngagementSummary(payload: IGetDashboardSummary) {
      const { data } = await reportAPI.DashboardServices.GetEngagementSummary(payload)
      return data || emptySummary()
    },
    async GetSignedInUsersReport(payload: IGetDashboardSummary) {
      const { data } = await reportAPI.DashboardServices.GetSignedInUsersReport(payload)
      return data || emptySummary()
    },
    async GetEmployeesEarnPointSummary(payload: IGetDashboardSummary) {
      const { data } = await reportAPI.DashboardServices.GetEmployeesEarnPointSummary(payload)
      return data || emptySummary()
    },
    async GetMobileSignedReport(payload: IGetMobileSignedInUsers, isAllTime: boolean = true) {
      const { data, status } = await reportAPI.DashboardServices.GetMobileSignedReport(payload)
      if (isAllTime) {
        return status ? data.mobileUsersCount : 0
      }
      return status ? data : { mobileUsersCount: 0, totalUsersCount: 0 }
    },
  },
})
